import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Filter, X, ChevronDown, Calendar, Users, GraduationCap, Globe, Award } from 'lucide-react';

const FilterBar = ({ onFiltersChange, totalUsers, users = [] }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [filters, setFilters] = useState({
    // Basic filters (all arrays for multi-select)
    role: [],
    gender: [],
    citizenship: [],
    firstLanguage: [],
    profileCompleted: [],
    
    // Academic filters
    fields: [],
    interests: [],
    preferredCountries: [],
    
    // Test score ranges
    greVerbal: { min: '', max: '' },
    greQuantitative: { min: '', max: '' },
    greWriting: { min: '', max: '' },
    toeflReading: { min: '', max: '' },
    toeflListening: { min: '', max: '' },
    toeflSpeaking: { min: '', max: '' },
    toeflWriting: { min: '', max: '' },
    ieltsListening: { min: '', max: '' },
    ieltsReading: { min: '', max: '' },
    ieltsSpeaking: { min: '', max: '' },
    ieltsWriting: { min: '', max: '' },
    ieltsOverall: { min: '', max: '' },
    
    // System filters (all arrays for multi-select)
    hasResume: [],
    googleLinked: [],
    activeUsers: [],
    dateRange: { start: '', end: '' },
    lastLoginRange: { start: '', end: '' },
    
    // Student-specific
    matchesRemaining: { min: '', max: '' },
    hasMatches: [],
    hasReferral: []
  });

  const [dropdownStates, setDropdownStates] = useState({});
  const dropdownRefs = useRef({});

  // Extract unique values from actual user data (handles both single values and arrays)
  const extractUniqueValues = (field, isArray = false) => {
    if (isArray) {
      // For array fields (like interests, preferredCountries)
      const allValues = users
        .map(user => user[field])
        .filter(value => value != null && Array.isArray(value))
        .flat();
      return [...new Set(allValues)]
        .filter(value => value != null && value !== '')
        .sort();
    } else {
      // For single value fields (like citizenship)
      return [...new Set(
        users
          .map(user => user[field])
          .filter(value => value != null && value !== '')
      )].sort();
    }
  };

  // Get actual values from users (memoized for performance)
  const citizenshipOptions = useMemo(() => extractUniqueValues('citizenship'), [users]);
  const interestOptions = useMemo(() => extractUniqueValues('interests', true), [users]);
  const countryOptions = useMemo(() => extractUniqueValues('preferredCountries', true), [users]);

  const languageOptions = [
    'English', 'Spanish', 'French', 'German', 'Italian', 'Portuguese',
    'Russian', 'Chinese', 'Japanese', 'Korean', 'Arabic', 'Hindi'
  ];

  const toggleDropdown = (dropdownName) => {
    setDropdownStates(prev => ({
      ...prev,
      [dropdownName]: !prev[dropdownName]
    }));
  };

  const closeDropdown = (dropdownName) => {
    setDropdownStates(prev => ({
      ...prev,
      [dropdownName]: false
    }));
  };

  const updateFilter = (filterPath, value) => {
    setFilters(prev => {
      const newFilters = { ...prev };
      const keys = filterPath.split('.');
      let current = newFilters;
      
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      return newFilters;
    });
  };

  const toggleArrayFilter = (filterPath, value) => {
    setFilters(prev => {
      const newFilters = { ...prev };
      const keys = filterPath.split('.');
      let current = newFilters;
      
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current = current[keys[i]];
      }
      
      const array = current[keys[keys.length - 1]] || [];
      const newArray = array.includes(value)
        ? array.filter(item => item !== value)
        : [...array, value];
      
      current[keys[keys.length - 1]] = newArray;
      return newFilters;
    });
  };

  const clearAllFilters = () => {
    setFilters({
      role: [],
      gender: [],
      citizenship: [],
      firstLanguage: [],
      profileCompleted: [],
      fields: [],
      interests: [],
      preferredCountries: [],
      greVerbal: { min: '', max: '' },
      greQuantitative: { min: '', max: '' },
      greWriting: { min: '', max: '' },
      toeflReading: { min: '', max: '' },
      toeflListening: { min: '', max: '' },
      toeflSpeaking: { min: '', max: '' },
      toeflWriting: { min: '', max: '' },
      ieltsListening: { min: '', max: '' },
      ieltsReading: { min: '', max: '' },
      ieltsSpeaking: { min: '', max: '' },
      ieltsWriting: { min: '', max: '' },
      ieltsOverall: { min: '', max: '' },
      hasResume: [],
      googleLinked: [],
      activeUsers: [],
      dateRange: { start: '', end: '' },
      lastLoginRange: { start: '', end: '' },
      matchesRemaining: { min: '', max: '' },
      hasMatches: [],
      hasReferral: []
    });
  };

  const getActiveFilterCount = () => {
    let count = 0;
    
    // All basic filters are now arrays
    if (filters.role.length > 0) count++;
    if (filters.gender.length > 0) count++;
    if (filters.citizenship.length > 0) count++;
    if (filters.firstLanguage.length > 0) count++;
    if (filters.profileCompleted.length > 0) count++;
    
    // Array filters
    if (filters.fields.length > 0) count++;
    if (filters.interests.length > 0) count++;
    if (filters.preferredCountries.length > 0) count++;
    
    // Range filters
    Object.keys(filters).forEach(key => {
      if (typeof filters[key] === 'object' && filters[key] !== null && !Array.isArray(filters[key])) {
        if (filters[key].min || filters[key].max) count++;
      }
    });
    
    // Other array filters
    if (filters.hasResume.length > 0) count++;
    if (filters.googleLinked.length > 0) count++;
    if (filters.activeUsers.length > 0) count++;
    if (filters.hasReferral.length > 0) count++;
    if (filters.hasMatches.length > 0) count++;
    
    return count;
  };

  useEffect(() => {
    onFiltersChange(filters);
  }, [filters, onFiltersChange]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is inside any dropdown content (not just the button)
      const clickedInsideDropdown = Object.keys(dropdownRefs.current).some(dropdownName => {
        const buttonRef = dropdownRefs.current[dropdownName];
        if (!buttonRef) return false;
        
        // Check if click is on the button or inside the dropdown content
        const dropdownContent = buttonRef.parentElement?.querySelector('[role="region"]') || 
                               buttonRef.parentElement?.querySelector('.absolute');
        
        return buttonRef.contains(event.target) || 
               (dropdownContent && dropdownContent.contains(event.target));
      });
      
      if (!clickedInsideDropdown) {
        // Close all open dropdowns
        Object.keys(dropdownStates).forEach(dropdownName => {
          if (dropdownStates[dropdownName]) {
            closeDropdown(dropdownName);
          }
        });
      }
    };

    // Only add listener if any dropdown is open
    if (Object.values(dropdownStates).some(isOpen => isOpen)) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [dropdownStates]);

  // Close dropdowns on escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        Object.keys(dropdownStates).forEach(dropdownName => {
          if (dropdownStates[dropdownName]) {
            closeDropdown(dropdownName);
          }
        });
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [dropdownStates]);

  const DropdownButton = ({ name, icon: Icon, label, count }) => (
    <button
      ref={el => dropdownRefs.current[name] = el}
      onClick={() => toggleDropdown(name)}
      className={`flex items-center space-x-2 px-3 py-2 text-sm border rounded-lg transition-colors ${
        dropdownStates[name] 
          ? 'bg-blue-50 border-blue-300 text-blue-700' 
          : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
      }`}
      aria-expanded={dropdownStates[name]}
      aria-haspopup="true"
    >
      <Icon size={16} />
      <span>{label}</span>
      {count > 0 && (
        <span className="bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full">
          {count}
        </span>
      )}
      <ChevronDown size={14} className={`transition-transform ${dropdownStates[name] ? 'rotate-180' : ''}`} />
    </button>
  );

  const Dropdown = ({ name, children, className = '' }) => {
    return dropdownStates[name] && (
      <div className={`absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto ${className}`}>
        {children}
      </div>
    );
  };

  const RangeInput = ({ label, minValue, maxValue, onMinChange, onMaxChange, placeholder = "Any" }) => (
    <div className="p-3 border-b border-gray-100 last:border-b-0">
      <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
      <div className="flex space-x-2">
        <input
          type="number"
          placeholder="Min"
          value={minValue}
          onChange={(e) => onMinChange(e.target.value)}
          className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          aria-label={`${label} minimum value`}
        />
        <span className="text-gray-500 self-center" aria-hidden="true">-</span>
        <input
          type="number"
          placeholder="Max"
          value={maxValue}
          onChange={(e) => onMaxChange(e.target.value)}
          className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          aria-label={`${label} maximum value`}
        />
      </div>
    </div>
  );

  const MultiSelect = ({ options, selectedValues, onToggle, placeholder }) => (
    <div className="p-3 border-b border-gray-100 last:border-b-0">
      <div className="max-h-48 overflow-y-auto" role="listbox" aria-label="Select options">
        {options.map(option => (
          <label key={option} className="flex items-center space-x-2 py-1 hover:bg-gray-50 cursor-pointer" onClick={(e) => e.stopPropagation()}>
            <input
              type="checkbox"
              checked={selectedValues.includes(option)}
              onChange={(e) => {
                e.stopPropagation();
                onToggle(option);
              }}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              aria-describedby={`${option}-description`}
            />
            <span className="text-sm text-gray-700" id={`${option}-description`}>{option}</span>
          </label>
        ))}
      </div>
    </div>
  );

  const activeFilterCount = getActiveFilterCount();

  return (
    <div className="bg-white border-b border-gray-200">
      {/* Filter Toggle Bar */}
      <div className="px-6 py-3 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center space-x-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Filter size={16} />
            <span>Filters</span>
            {activeFilterCount > 0 && (
              <span className="bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full">
                {activeFilterCount}
              </span>
            )}
          </button>
          
          {activeFilterCount > 0 && (
            <button
              onClick={clearAllFilters}
              className="flex items-center space-x-1 px-2 py-1 text-sm text-gray-600 hover:text-gray-800"
            >
              <X size={14} />
              <span>Clear All</span>
            </button>
          )}
        </div>
        
        <div className="text-sm text-gray-600">
          Showing {totalUsers} users
        </div>
      </div>

      {/* Expanded Filter Panel */}
      {isExpanded && (
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200" role="region" aria-label="Advanced filters">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            
            {/* Basic Information Filters */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-800 flex items-center">
                <Users size={16} className="mr-2" />
                Basic Info
              </h3>
              
              <div className="relative">
                <DropdownButton
                  name="role"
                  icon={Users}
                  label="Role"
                  count={filters.role.length}
                />
                <Dropdown name="role" className="w-48">
                  <MultiSelect
                    options={['student', 'professor', 'admin'].map(r => r.charAt(0).toUpperCase() + r.slice(1))}
                    selectedValues={filters.role.map(r => r.charAt(0).toUpperCase() + r.slice(1))}
                    onToggle={(value) => {
                      const roleValue = value.toLowerCase();
                      toggleArrayFilter('role', roleValue);
                    }}
                  />
                </Dropdown>
              </div>

              <div className="relative">
                <DropdownButton
                  name="gender"
                  icon={Users}
                  label="Gender"
                  count={filters.gender.length}
                />
                <Dropdown name="gender" className="w-48">
                  <MultiSelect
                    options={['Male', 'Female', 'Other', 'Prefer not to say']}
                    selectedValues={filters.gender.map(g => {
                      if (!g || g === '') return 'Prefer not to say';
                      return g.charAt(0).toUpperCase() + g.slice(1).toLowerCase();
                    })}
                    onToggle={(value) => {
                      const genderValue = value === 'Prefer not to say' ? '' : value.toLowerCase();
                      toggleArrayFilter('gender', genderValue);
                    }}
                  />
                </Dropdown>
              </div>

              <div className="relative">
                <DropdownButton
                  name="citizenship"
                  icon={Globe}
                  label="Citizenship"
                  count={filters.citizenship.length}
                />
                <Dropdown name="citizenship" className="w-64 max-h-96 overflow-y-auto">
                  {citizenshipOptions.length > 0 ? (
                    <MultiSelect
                      options={citizenshipOptions}
                      selectedValues={filters.citizenship}
                      onToggle={(value) => toggleArrayFilter('citizenship', value)}
                    />
                  ) : (
                    <div className="p-3">
                      <div className="px-3 py-2 text-sm text-gray-500">No citizenship data available</div>
                    </div>
                  )}
                </Dropdown>
              </div>

              <div className="relative">
                <DropdownButton
                  name="profileCompleted"
                  icon={Award}
                  label="Profile"
                  count={filters.profileCompleted.length}
                />
                <Dropdown name="profileCompleted" className="w-48">
                  <MultiSelect
                    options={['Completed', 'Incomplete']}
                    selectedValues={filters.profileCompleted.map(v => v === 'true' ? 'Completed' : 'Incomplete')}
                    onToggle={(value) => {
                      const boolValue = value === 'Completed' ? 'true' : 'false';
                      toggleArrayFilter('profileCompleted', boolValue);
                    }}
                  />
                </Dropdown>
              </div>
            </div>

            {/* Academic Filters */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-800 flex items-center">
                <GraduationCap size={16} className="mr-2" />
                Academic
              </h3>
              
              <div className="relative">
                <DropdownButton
                  name="interests"
                  icon={GraduationCap}
                  label="Interests"
                  count={filters.interests.length}
                />
                <Dropdown name="interests" className="w-64 max-h-96 overflow-y-auto">
                  {interestOptions.length > 0 ? (
                    <MultiSelect
                      options={interestOptions}
                      selectedValues={filters.interests}
                      onToggle={(value) => toggleArrayFilter('interests', value)}
                    />
                  ) : (
                    <div className="p-3">
                      <div className="px-3 py-2 text-sm text-gray-500">No interest data available</div>
                    </div>
                  )}
                </Dropdown>
              </div>

              <div className="relative">
                <DropdownButton
                  name="preferredCountries"
                  icon={Globe}
                  label="Countries"
                  count={filters.preferredCountries.length}
                />
                <Dropdown name="preferredCountries" className="w-64 max-h-96 overflow-y-auto">
                  {countryOptions.length > 0 ? (
                    <MultiSelect
                      options={countryOptions}
                      selectedValues={filters.preferredCountries}
                      onToggle={(value) => toggleArrayFilter('preferredCountries', value)}
                    />
                  ) : (
                    <div className="p-3">
                      <div className="px-3 py-2 text-sm text-gray-500">No country data available</div>
                    </div>
                  )}
                </Dropdown>
              </div>
            </div>

            {/* Test Scores */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-800 flex items-center">
                <Award size={16} className="mr-2" />
                Test Scores
              </h3>
              
              <div className="relative">
                <DropdownButton
                  name="gre"
                  icon={Award}
                  label="GRE Scores"
                  count={Object.values(filters.greVerbal).some(v => v) + Object.values(filters.greQuantitative).some(v => v) + Object.values(filters.greWriting).some(v => v)}
                />
                <Dropdown name="gre" className="w-64">
                  <RangeInput
                    label="Verbal (130-170)"
                    minValue={filters.greVerbal.min}
                    maxValue={filters.greVerbal.max}
                    onMinChange={(value) => updateFilter('greVerbal.min', value)}
                    onMaxChange={(value) => updateFilter('greVerbal.max', value)}
                  />
                  <RangeInput
                    label="Quantitative (130-170)"
                    minValue={filters.greQuantitative.min}
                    maxValue={filters.greQuantitative.max}
                    onMinChange={(value) => updateFilter('greQuantitative.min', value)}
                    onMaxChange={(value) => updateFilter('greQuantitative.max', value)}
                  />
                  <RangeInput
                    label="Writing (0-6)"
                    minValue={filters.greWriting.min}
                    maxValue={filters.greWriting.max}
                    onMinChange={(value) => updateFilter('greWriting.min', value)}
                    onMaxChange={(value) => updateFilter('greWriting.max', value)}
                  />
                </Dropdown>
              </div>

              <div className="relative">
                <DropdownButton
                  name="toefl"
                  icon={Award}
                  label="TOEFL Scores"
                  count={Object.values(filters.toeflReading).some(v => v) + Object.values(filters.toeflListening).some(v => v) + Object.values(filters.toeflSpeaking).some(v => v) + Object.values(filters.toeflWriting).some(v => v)}
                />
                <Dropdown name="toefl" className="w-64">
                  <RangeInput
                    label="Reading (0-30)"
                    minValue={filters.toeflReading.min}
                    maxValue={filters.toeflReading.max}
                    onMinChange={(value) => updateFilter('toeflReading.min', value)}
                    onMaxChange={(value) => updateFilter('toeflReading.max', value)}
                  />
                  <RangeInput
                    label="Listening (0-30)"
                    minValue={filters.toeflListening.min}
                    maxValue={filters.toeflListening.max}
                    onMinChange={(value) => updateFilter('toeflListening.min', value)}
                    onMaxChange={(value) => updateFilter('toeflListening.max', value)}
                  />
                  <RangeInput
                    label="Speaking (0-30)"
                    minValue={filters.toeflSpeaking.min}
                    maxValue={filters.toeflSpeaking.max}
                    onMinChange={(value) => updateFilter('toeflSpeaking.min', value)}
                    onMaxChange={(value) => updateFilter('toeflSpeaking.max', value)}
                  />
                  <RangeInput
                    label="Writing (0-30)"
                    minValue={filters.toeflWriting.min}
                    maxValue={filters.toeflWriting.max}
                    onMinChange={(value) => updateFilter('toeflWriting.min', value)}
                    onMaxChange={(value) => updateFilter('toeflWriting.max', value)}
                  />
                </Dropdown>
              </div>

              <div className="relative">
                <DropdownButton
                  name="ielts"
                  icon={Award}
                  label="IELTS Scores"
                  count={Object.values(filters.ieltsListening).some(v => v) + Object.values(filters.ieltsReading).some(v => v) + Object.values(filters.ieltsSpeaking).some(v => v) + Object.values(filters.ieltsWriting).some(v => v) + Object.values(filters.ieltsOverall).some(v => v)}
                />
                <Dropdown name="ielts" className="w-64">
                  <RangeInput
                    label="Listening (0-9)"
                    minValue={filters.ieltsListening.min}
                    maxValue={filters.ieltsListening.max}
                    onMinChange={(value) => updateFilter('ieltsListening.min', value)}
                    onMaxChange={(value) => updateFilter('ieltsListening.max', value)}
                  />
                  <RangeInput
                    label="Reading (0-9)"
                    minValue={filters.ieltsReading.min}
                    maxValue={filters.ieltsReading.max}
                    onMinChange={(value) => updateFilter('ieltsReading.min', value)}
                    onMaxChange={(value) => updateFilter('ieltsReading.max', value)}
                  />
                  <RangeInput
                    label="Speaking (0-9)"
                    minValue={filters.ieltsSpeaking.min}
                    maxValue={filters.ieltsSpeaking.max}
                    onMinChange={(value) => updateFilter('ieltsSpeaking.min', value)}
                    onMaxChange={(value) => updateFilter('ieltsSpeaking.max', value)}
                  />
                  <RangeInput
                    label="Writing (0-9)"
                    minValue={filters.ieltsWriting.min}
                    maxValue={filters.ieltsWriting.max}
                    onMinChange={(value) => updateFilter('ieltsWriting.min', value)}
                    onMaxChange={(value) => updateFilter('ieltsWriting.max', value)}
                  />
                  <RangeInput
                    label="Overall (0-9)"
                    minValue={filters.ieltsOverall.min}
                    maxValue={filters.ieltsOverall.max}
                    onMinChange={(value) => updateFilter('ieltsOverall.min', value)}
                    onMaxChange={(value) => updateFilter('ieltsOverall.max', value)}
                  />
                </Dropdown>
              </div>
            </div>

            {/* System & Student Filters */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-800 flex items-center">
                <Calendar size={16} className="mr-2" />
                System & Student
              </h3>
              
              <div className="relative">
                <DropdownButton
                  name="system"
                  icon={Calendar}
                  label="System"
                  count={filters.hasResume.length + filters.googleLinked.length}
                />
                <Dropdown name="system" className="w-48">
                  <div className="p-3 border-b border-gray-100">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Resume</label>
                    <MultiSelect
                      options={['Has Resume', 'No Resume']}
                      selectedValues={filters.hasResume.map(v => v === 'true' ? 'Has Resume' : 'No Resume')}
                      onToggle={(value) => {
                        const boolValue = value === 'Has Resume' ? 'true' : 'false';
                        toggleArrayFilter('hasResume', boolValue);
                      }}
                    />
                  </div>
                  <div className="p-3">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Google Account</label>
                    <MultiSelect
                      options={['Linked', 'Not Linked']}
                      selectedValues={filters.googleLinked.map(v => v === 'true' ? 'Linked' : 'Not Linked')}
                      onToggle={(value) => {
                        const boolValue = value === 'Linked' ? 'true' : 'false';
                        toggleArrayFilter('googleLinked', boolValue);
                      }}
                    />
                  </div>
                </Dropdown>
              </div>

              <div className="relative">
                <DropdownButton
                  name="matches"
                  icon={Users}
                  label="Student Matches"
                  count={(filters.matchesRemaining.min || filters.matchesRemaining.max ? 1 : 0) + filters.hasMatches.length}
                />
                <Dropdown name="matches" className="w-64">
                  <RangeInput
                    label="Matches Remaining"
                    minValue={filters.matchesRemaining.min}
                    maxValue={filters.matchesRemaining.max}
                    onMinChange={(value) => updateFilter('matchesRemaining.min', value)}
                    onMaxChange={(value) => updateFilter('matchesRemaining.max', value)}
                  />
                  <div className="p-3 border-b border-gray-100 last:border-b-0">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Has Matches</label>
                    <MultiSelect
                      options={['Has Matches', 'No Matches']}
                      selectedValues={filters.hasMatches.map(v => v === 'true' ? 'Has Matches' : 'No Matches')}
                      onToggle={(value) => {
                        const boolValue = value === 'Has Matches' ? 'true' : 'false';
                        toggleArrayFilter('hasMatches', boolValue);
                      }}
                    />
                  </div>
                </Dropdown>
              </div>

              <div className="relative">
                <DropdownButton
                  name="activeUsers"
                  icon={Users}
                  label="Active Users"
                  count={filters.activeUsers.length}
                />
                <Dropdown name="activeUsers" className="w-48">
                  <MultiSelect
                    options={['Active', 'Not Active']}
                    selectedValues={filters.activeUsers.map(v => v === 'true' ? 'Active' : 'Not Active')}
                    onToggle={(value) => {
                      const boolValue = value === 'Active' ? 'true' : 'false';
                      toggleArrayFilter('activeUsers', boolValue);
                    }}
                  />
                </Dropdown>
              </div>

              <div className="relative">
                <DropdownButton
                  name="hasReferral"
                  icon={Users}
                  label="Has Referral"
                  count={filters.hasReferral.length}
                />
                <Dropdown name="hasReferral" className="w-48">
                  <MultiSelect
                    options={['Has Referrals', 'No Referrals']}
                    selectedValues={filters.hasReferral.map(v => v === 'true' ? 'Has Referrals' : 'No Referrals')}
                    onToggle={(value) => {
                      const boolValue = value === 'Has Referrals' ? 'true' : 'false';
                      toggleArrayFilter('hasReferral', boolValue);
                    }}
                  />
                </Dropdown>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterBar;
